import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Spinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { MEDICAL_CONDITION, COMMERCIAL_GESTURE } from 'constants/subscriptionCancellationReasons';

import { BOOKINGS_SEARCH_TYPE } from 'constants/clubEurostar';
import { GET_SUBSCRIPTION_BOOKINGS } from 'store/queries';
import { getProducts, hasNotCancelledThalysPassTickets } from '../Cancellations.utils';

import ProrataCancellation from '../ProrataCancellation';
import ManualCancellation from '../ManualCancellation';
import { CancellationReasons } from './CancellationReasons';

export const CancellationModals = (props) => {
  const { isThalysAdminUserOffice, bookingWithin14DaysCoolingOffPeriod, intl, customerId, travelPassReference, userCanCancelSubscription } =
    props;
  const [reasonCode, setReasonCode] = useState('');
  const [hasFutureBookings, setHasfutureBookings] = useState(false);
  const [hasPastBookings, setHasPastBookings] = useState(false);
  const [isManualCancellation, setIsManualCancellation] = useState(false);
  const [isProrataCancellation, setProrataCancellation] = useState(false);

  const defaultQueryParams = {
    customerId,
    travelPassReference,
    bookingSearchType: BOOKINGS_SEARCH_TYPE.UPCOMING_BOOKINGS,
    page: 1,
    size: 1
  };

  const {
    data: upcomingBookings,
    loading: loadingFutureSubscriptionBookings,
    error: errorFutureSubscriptionBookings
  } = useQuery(GET_SUBSCRIPTION_BOOKINGS, {
    variables: { ...defaultQueryParams },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  const {
    data: pastBookings,
    loading: loadingPastSubscriptionBookings,
    error: errorPastSubscriptionBookings
  } = useQuery(GET_SUBSCRIPTION_BOOKINGS, {
    variables: { ...defaultQueryParams, bookingSearchType: BOOKINGS_SEARCH_TYPE.PAST_BOOKINGS },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  const [getFutureSubscriptionBookings, { loading: loadingFuture, error: errorFuture }] = useLazyQuery(GET_SUBSCRIPTION_BOOKINGS, {
    onCompleted: (data) => setSubscriptionBookingsState(data?.profileSubscriptionBookings?.items, BOOKINGS_SEARCH_TYPE.UPCOMING_BOOKINGS)
  });
  const [getPastSubscriptionBookings, { loading: loadingPast, error: errorPast }] = useLazyQuery(GET_SUBSCRIPTION_BOOKINGS, {
    onCompleted: (data) => setSubscriptionBookingsState(data?.profileSubscriptionBookings?.items, BOOKINGS_SEARCH_TYPE.PAST_BOOKINGS)
  });

  const handleSubscriptionBookings = (data, type) => {
    setSubscriptionBookingsState(data?.items, type);
    const size = data?.pagination?.totalItems;
    if (size > 1) {
      type === BOOKINGS_SEARCH_TYPE.UPCOMING_BOOKINGS
        ? getFutureSubscriptionBookings({
            variables: { ...defaultQueryParams, size }
          })
        : getPastSubscriptionBookings({
            variables: { ...defaultQueryParams, bookingSearchType: BOOKINGS_SEARCH_TYPE.PAST_BOOKINGS, size }
          });
    }
  };

  const setSubscriptionBookingsState = (data, type) => {
    const products = getProducts(data);
    const notCancelledThalysPassTickets = hasNotCancelledThalysPassTickets(products);

    if (notCancelledThalysPassTickets) {
      return type === BOOKINGS_SEARCH_TYPE.PAST_BOOKINGS ? setHasPastBookings(true) : setHasfutureBookings(true);
    }
  };

  const handleCancellationClick = () => {
    if (bookingWithin14DaysCoolingOffPeriod) {
      return setProrataCancellation(true);
    }

    if (reasonCode === MEDICAL_CONDITION || reasonCode === COMMERCIAL_GESTURE) return setProrataCancellation(true);
    return setIsManualCancellation(true);
  };

  const loading = loadingFutureSubscriptionBookings || loadingPastSubscriptionBookings || loadingFuture || loadingPast;
  const error = errorPastSubscriptionBookings || errorFutureSubscriptionBookings || errorFuture || errorPast;

  useEffect(() => {
    handleSubscriptionBookings(upcomingBookings?.profileSubscriptionBookings, BOOKINGS_SEARCH_TYPE.UPCOMING_BOOKINGS);
  }, [upcomingBookings]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleSubscriptionBookings(pastBookings?.profileSubscriptionBookings, BOOKINGS_SEARCH_TYPE.PAST_BOOKINGS);
  }, [pastBookings]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    throw new AgentPortalError(error);
  }

  return (
    <>
      {loading && <Spinner />}
      {!loading && hasFutureBookings && (
        <div data-testid="subscriptions.cancelTicketsFirst">
          <FormattedMessage id="subscriptions.cancelTicketsFirst" />
        </div>
      )}
      {!loading && !hasFutureBookings && (
        <>
          {!isManualCancellation && !isProrataCancellation && (
            <CancellationReasons
              bookingWithin14DaysCoolingOffPeriod={bookingWithin14DaysCoolingOffPeriod}
              handleChange={(reason) => setReasonCode(reason)}
              handleClick={handleCancellationClick}
              reasonCode={reasonCode}
              userCanCancelSubscription={userCanCancelSubscription}
              isThalysAdminUserOffice={isThalysAdminUserOffice}
              intl={intl}
              hasPastBookings={hasPastBookings}
            />
          )}

          {isProrataCancellation && <ProrataCancellation {...props} reason={reasonCode} hasPastBookings={hasPastBookings} />}
          {isManualCancellation && <ManualCancellation {...props} reason={reasonCode} hasPastBookings={hasPastBookings} />}
        </>
      )}
    </>
  );
};

CancellationModals.propTypes = {
  itemRef: PropTypes.string,
  passenger: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string
  }),
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  name: PropTypes.string,
  currency: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bookingReference: PropTypes.string,
  confirmedTimestamp: PropTypes.string,
  bookingStatus: PropTypes.string,
  hideCancelButton: PropTypes.bool,
  onClose: PropTypes.func,
  reason: PropTypes.string,
  bookingWithin14DaysCoolingOffPeriod: PropTypes.bool,
  membershipNumber: PropTypes.string,
  travelPassReference: PropTypes.string,
  customerId: PropTypes.string,
  isThalysAdminUserOffice: PropTypes.bool,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }),
  userCanCancelSubscription: PropTypes.bool
};
